import Phaser from "phaser";
import type { Controls } from "./Controls";
import type { AttackKind } from "../config";

/**
 * Touch controls v4 — два одинаковых стика по краям экрана.
 *
 *   ◯ ЛЕВЫЙ СТИК   ────────       ◯ ПРАВЫЙ СТИК
 *     движение                       удары (по направлению)
 *     прыжок ↑                       вверх    → апперкот
 *     присед ↓                       вправо   → прямой
 *                                    вниз     → тяжёлый
 *                                    влево    → хук
 *                                    тап в центр → джеб
 *
 *   [BLK] (по центру внизу)        [RUN] (по центру внизу)
 */
export class TouchControls implements Controls {
  readonly source: string = "TOUCH";

  private scene: Phaser.Scene;
  private pressed: Record<string, boolean> = {};
  private prev: Record<string, boolean> = {};
  private container!: Phaser.GameObjects.Container;

  // ── левый стик (движение) ──
  private lKnob!: Phaser.GameObjects.Arc;
  private lBaseX = 0; private lBaseY = 0;
  private lR = 80;
  private lPid: number | null = null;
  private lThreshold = 0.35;

  // ── правый стик (удары) ──
  private rKnob!: Phaser.GameObjects.Arc;
  private rBaseX = 0; private rBaseY = 0;
  private rR = 80;
  private rPid: number | null = null;
  private rTapDeadzone = 0.25;   // ниже этого радиуса — считается tap = jab

  // ── attack edge-trigger ──
  private pendingKind: AttackKind | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    scene.input.addPointer(6);

    const W = scene.scale.gameSize.width;
    const H = scene.scale.gameSize.height;
    this.container = scene.add.container(0, 0).setDepth(1000);

    this.buildLeftStick(W, H);
    this.buildRightStick(W, H);
    this.buildBlockButton(W, H);
    this.buildRunButton(W, H);
    this.buildStartButton(W, H);
  }

  // ── общий конструктор «стика» (track + knob + cross) ────────────────────
  private buildStick(cx: number, cy: number, R: number, accent: number): {
    knob: Phaser.GameObjects.Arc;
    hit: Phaser.GameObjects.Arc;
  } {
    const trackOuter = this.scene.add.circle(cx, cy, R + 6, 0x000000, 0.55)
      .setStrokeStyle(3, accent, 0.85);
    const trackInner = this.scene.add.circle(cx, cy, R, 0x1a0505, 0.45)
      .setStrokeStyle(1, 0xffffff, 0.3);
    const cross = this.scene.add.graphics();
    cross.lineStyle(2, 0xffffff, 0.18);
    cross.lineBetween(cx - R + 8, cy, cx + R - 8, cy);
    cross.lineBetween(cx, cy - R + 8, cx, cy + R - 8);

    const knob = this.scene.add.circle(cx, cy, 32, accent, 0.9)
      .setStrokeStyle(3, 0xffd700, 1.0);
    const hit = this.scene.add.circle(cx, cy, R + 30, 0xffffff, 0.001)
      .setInteractive({ useHandCursor: false });

    this.container.add([trackOuter, trackInner, cross, knob, hit]);
    return { knob, hit };
  }

  // ── ЛЕВЫЙ стик (движение) ────────────────────────────────────────────────
  private buildLeftStick(_W: number, H: number) {
    this.lBaseX = 100; this.lBaseY = H - 100;
    const { knob, hit } = this.buildStick(this.lBaseX, this.lBaseY, this.lR, 0xc41e3a);
    this.lKnob = knob;

    hit.on("pointerdown", (p: Phaser.Input.Pointer) => {
      if (this.lPid !== null) return;
      this.lPid = p.id;
      this.updateLeft(p.x, p.y);
    });
    hit.on("pointermove", (p: Phaser.Input.Pointer) => {
      if (this.lPid === p.id) this.updateLeft(p.x, p.y);
    });
    const release = (p: Phaser.Input.Pointer) => {
      if (this.lPid === p.id) { this.lPid = null; this.resetLeft(); }
    };
    hit.on("pointerup", release);
    hit.on("pointerupoutside", release);
    this.scene.input.on("pointerup", release);
  }

  private updateLeft(px: number, py: number) {
    const dx = px - this.lBaseX, dy = py - this.lBaseY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const max = this.lR;
    const clamped = Math.min(dist, max);
    const ang = Math.atan2(dy, dx);
    const kx = this.lBaseX + Math.cos(ang) * clamped;
    const ky = this.lBaseY + Math.sin(ang) * clamped;
    this.lKnob.setPosition(kx, ky);

    const nx = (kx - this.lBaseX) / max;
    const ny = (ky - this.lBaseY) / max;
    const t = this.lThreshold;
    this.pressed.left  = nx < -t;
    this.pressed.right = nx >  t;
    this.pressed.up    = ny < -t;
    this.pressed.down  = ny >  t;
  }

  private resetLeft() {
    this.lKnob.setPosition(this.lBaseX, this.lBaseY);
    this.pressed.left = this.pressed.right = this.pressed.up = this.pressed.down = false;
  }

  // ── ПРАВЫЙ стик (удары) ──────────────────────────────────────────────────
  private buildRightStick(W: number, H: number) {
    this.rBaseX = W - 100; this.rBaseY = H - 100;
    const { knob, hit } = this.buildStick(this.rBaseX, this.rBaseY, this.rR, 0xb87800);
    this.rKnob = knob;

    hit.on("pointerdown", (p: Phaser.Input.Pointer) => {
      if (this.rPid !== null) return;
      this.rPid = p.id;
      // Удар выпускается сразу на pointerdown в направлении касания.
      this.fireAttackFromPointer(p.x, p.y);
      this.updateRightKnob(p.x, p.y);
    });
    hit.on("pointermove", (p: Phaser.Input.Pointer) => {
      if (this.rPid === p.id) this.updateRightKnob(p.x, p.y);
    });
    const release = (p: Phaser.Input.Pointer) => {
      if (this.rPid === p.id) {
        this.rPid = null;
        this.rKnob.setPosition(this.rBaseX, this.rBaseY);
      }
    };
    hit.on("pointerup", release);
    hit.on("pointerupoutside", release);
    this.scene.input.on("pointerup", release);
  }

  private updateRightKnob(px: number, py: number) {
    const dx = px - this.rBaseX, dy = py - this.rBaseY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const max = this.rR;
    const clamped = Math.min(dist, max);
    const ang = Math.atan2(dy, dx);
    this.rKnob.setPosition(
      this.rBaseX + Math.cos(ang) * clamped,
      this.rBaseY + Math.sin(ang) * clamped,
    );
  }

  private fireAttackFromPointer(px: number, py: number) {
    const dx = px - this.rBaseX, dy = py - this.rBaseY;
    const len = Math.sqrt(dx * dx + dy * dy);
    const max = this.rR;
    if (len < this.rTapDeadzone * max) {
      this.pendingKind = "jab";
      return;
    }
    const ang = Math.atan2(dy, dx); // 0=right, π/2=down, -π/2=up
    const Q = Math.PI / 4;
    if (ang >= -Q && ang < Q)            this.pendingKind = "straight";   // → forward
    else if (ang >= Q && ang < 3 * Q)    this.pendingKind = "hp";         // ↓ down — heavy
    else if (ang >= -3 * Q && ang < -Q)  this.pendingKind = "uppercut";   // ↑ up
    else                                  this.pendingKind = "hp";         // ← back hook
  }

  // ── BLK ──
  private buildBlockButton(W: number, H: number) {
    const x = W / 2 + 80, y = H - 50;
    const R = 30;
    const ring = this.scene.add.circle(x, y, R + 4, 0x000000, 0).setStrokeStyle(3, 0x88ccff, 0.95);
    const btn = this.scene.add.circle(x, y, R, 0x2a4a8a, 0.78)
      .setStrokeStyle(2, 0x000000, 0.5)
      .setInteractive({ useHandCursor: false });
    const txt = this.scene.add.text(x, y, "BLK", {
      fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
      stroke: "#000", strokeThickness: 3,
    }).setOrigin(0.5);
    const press = () => {
      this.pressed.block = true;
      btn.setAlpha(1); btn.setScale(0.92);
      ring.setStrokeStyle(4, 0x88ccff, 1.0);
    };
    const release = () => {
      this.pressed.block = false;
      btn.setAlpha(0.78); btn.setScale(1.0);
      ring.setStrokeStyle(3, 0x88ccff, 0.95);
    };
    btn.on("pointerdown", press);
    btn.on("pointerup", release);
    btn.on("pointerupoutside", release);
    btn.on("pointerout", release);
    this.container.add([ring, btn, txt]);
  }

  // ── RUN ──
  private buildRunButton(W: number, H: number) {
    const x = W / 2 - 80, y = H - 50;
    const R = 30;
    const ring = this.scene.add.circle(x, y, R + 4, 0x000000, 0).setStrokeStyle(3, 0x44ff44, 0.95);
    const btn = this.scene.add.circle(x, y, R, 0x2a6a2a, 0.78)
      .setStrokeStyle(2, 0x000000, 0.5)
      .setInteractive({ useHandCursor: false });
    const txt = this.scene.add.text(x, y, "RUN", {
      fontFamily: "Arial Black", fontSize: "14px", color: "#ffffff",
      stroke: "#000", strokeThickness: 3,
    }).setOrigin(0.5);
    const press = () => { this.pressed.run = true; btn.setAlpha(1); ring.setStrokeStyle(4, 0x44ff44, 1.0); };
    const release = () => { this.pressed.run = false; btn.setAlpha(0.78); ring.setStrokeStyle(3, 0x44ff44, 0.95); };
    btn.on("pointerdown", press);
    btn.on("pointerup", release);
    btn.on("pointerupoutside", release);
    btn.on("pointerout", release);
    this.container.add([ring, btn, txt]);
  }

  private buildStartButton(W: number, _H: number) {
    const x = W - 50, y = 50;
    const btn = this.scene.add.circle(x, y, 22, 0x44ff44, 0.7)
      .setStrokeStyle(2, 0xffffff, 0.9)
      .setInteractive({ useHandCursor: false });
    const txt = this.scene.add.text(x, y, "▶", {
      fontFamily: "Arial Black", fontSize: "18px", color: "#ffffff",
    }).setOrigin(0.5);
    btn.on("pointerdown", () => { this.pressed.start = true; btn.setAlpha(1); });
    btn.on("pointerup",   () => { this.pressed.start = false; btn.setAlpha(0.7); });
    btn.on("pointerout",  () => { this.pressed.start = false; btn.setAlpha(0.7); });
    this.container.add([btn, txt]);
  }

  setVisible(v: boolean) { this.container.setVisible(v); }
  destroy() { this.container.destroy(true); }

  update(_time: number): void {
    this.prev = { ...this.pressed };
  }

  isLeft()  { return !!this.pressed.left; }
  isRight() { return !!this.pressed.right; }
  isUp()    { return !!this.pressed.up; }
  isDown()  { return !!this.pressed.down; }
  // Кнопок LP/HP/LK/HK больше нет — Fighter использует attackKind() напрямую.
  isPunch() { return false; }
  isKick()  { return false; }
  isBlock() { return !!this.pressed.block; }
  isRun()   { return !!this.pressed.run; }

  attackKind(): AttackKind | null {
    if (!this.pendingKind) return null;
    const k = this.pendingKind;
    this.pendingKind = null;
    return k;
  }

  justPressed(action: string): boolean {
    return !!this.pressed[action] && !this.prev[action];
  }
}

export function isTouchDevice(): boolean {
  return ("ontouchstart" in window) ||
         (navigator.maxTouchPoints > 0) ||
         /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}
